export type ThemePreference = "system" | "light" | "dark";

/** Also read by the inline script in index.html, which applies the theme before first paint. */
export const THEME_KEY = "editor.theme";

const PREFERENCES: readonly ThemePreference[] = ["system", "light", "dark"];

/** This page's choice once made, so blocked storage or another tab's choice cannot change it. */
let current: ThemePreference | null = null;

export function readThemePreference(): ThemePreference {
  if (current !== null) return current;
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    return PREFERENCES.find((value) => value === stored) ?? "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

export function applyTheme(preference: ThemePreference): void {
  const dark = preference === "dark" || (preference === "system" && systemPrefersDark());
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

export function setThemePreference(preference: ThemePreference): void {
  current = preference;
  try {
    window.localStorage.setItem(THEME_KEY, preference);
  } catch {
    // Storage blocked or full: the choice applies to this page only.
  }
  applyTheme(preference);
}

export function followSystemTheme(): void {
  current = readThemePreference();
  window.matchMedia?.("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (readThemePreference() === "system") applyTheme("system");
  });
}
